import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Loader2,
  Activity,
  Users,
  Clock,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  RefreshCw,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";

import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/shared/ui/tabs";
import { PageContent } from "@/shared/widgets/layout/PageContent";
import { DateRangePicker } from "@/shared/widgets/DateRangePicker";
import { useDefaultDatePresets } from "@/shared/widgets/DateRangePicker/presets";
import { toast } from "sonner";

type AmpSeriesResponse = {
  ok: boolean;
  status: number;
  data?: {
    data?: {
      series?: number[][];
      seriesLabels?: (string | number)[];
      xValues?: string[];
    };
  };
  error?: unknown;
};

type RetentionData = {
  cohort_sizes?: number[];
  r_values?: number[][];
  dates?: string[];
};
type AmpRetentionResponse = {
  ok: boolean;
  data?: { data?: RetentionData };
};

async function callAmplitude<T = unknown>(payload: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke("amplitude-analytics", {
    body: payload,
  });
  if (error) throw error;
  return data as T;
}

function ampDateToISO(s: string): string {
  // Amplitude returns dates as "YYYY-MM-DD" in xValues
  if (s?.length === 10) return s;
  return s;
}

function sumLast(values: number[] | undefined): number {
  if (!values?.length) return 0;
  return values.reduce((a, b) => a + b, 0);
}

function avgLast(values: number[] | undefined): number {
  if (!values?.length) return 0;
  return Math.round(sumLast(values) / values.length);
}

export default function AmplitudeAnalyticsPage() {
  const { t } = useTranslation("amplitude");
  const presets = useDefaultDatePresets();

  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const today = new Date();
    const start = new Date();
    start.setDate(today.getDate() - 30);
    return { from: start, to: today };
  });

  const [status, setStatus] = useState<"checking" | "ok" | "error">("checking");
  const [statusError, setStatusError] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);
  const [dau, setDau] = useState<{ x: string[]; y: number[] }>({ x: [], y: [] });
  const [wau, setWau] = useState<{ x: string[]; y: number[] }>({ x: [], y: [] });
  const [mau, setMau] = useState<{ x: string[]; y: number[] }>({ x: [], y: [] });
  const [newUsers, setNewUsers] = useState<{ x: string[]; y: number[] }>({ x: [], y: [] });
  const [sessions, setSessions] = useState<{ x: string[]; y: number[] }>({ x: [], y: [] });
  const [sessionLen, setSessionLen] = useState<{ x: string[]; y: number[] }>({ x: [], y: [] });
  const [events, setEvents] = useState<{ x: string[]; y: number[] }>({ x: [], y: [] });
  const [retention, setRetention] = useState<RetentionData | null>(null);

  const params = useMemo(() => {
    const startStr = dateRange.from
      ? format(dateRange.from, "yyyy-MM-dd")
      : undefined;
    const endStr = dateRange.to
      ? format(dateRange.to, "yyyy-MM-dd")
      : undefined;
    return { start: startStr, end: endStr };
  }, [dateRange]);

  async function checkStatus() {
    setStatus("checking");
    setStatusError(null);
    try {
      const r = await callAmplitude<{ configured: boolean; ok: boolean; error?: unknown }>({
        action: "status",
        ...params,
      });
      if (r?.configured && r.ok) {
        setStatus("ok");
      } else {
        setStatus("error");
        setStatusError(
          typeof r?.error === "string" ? r.error : JSON.stringify(r?.error ?? "unknown"),
        );
      }
    } catch (e) {
      setStatus("error");
      setStatusError(String((e as Error)?.message ?? e));
    }
  }

  function parseSeries(r: AmpSeriesResponse) {
    const series = r?.data?.data?.series ?? [];
    const xs = r?.data?.data?.xValues ?? [];
    const y = series[0] ?? [];
    return { x: xs.map(ampDateToISO), y };
  }

  async function loadAll() {
    if (status !== "ok") return;
    setLoading(true);
    try {
      const [d, w, m, nu, sess, sl, ev, ret] = await Promise.all([
        callAmplitude<AmpSeriesResponse>({ action: "active_users", interval: 1, ...params }),
        callAmplitude<AmpSeriesResponse>({ action: "active_users", interval: 7, ...params }),
        callAmplitude<AmpSeriesResponse>({ action: "active_users", interval: 30, ...params }),
        callAmplitude<AmpSeriesResponse>({ action: "new_users", interval: 1, ...params }),
        callAmplitude<AmpSeriesResponse>({ action: "sessions", metric: "sessions", ...params }),
        callAmplitude<AmpSeriesResponse>({ action: "sessions", metric: "avg_session_length", ...params }),
        callAmplitude<AmpSeriesResponse>({ action: "event_segmentation", ...params }),
        callAmplitude<AmpRetentionResponse>({ action: "retention", ...params }),
      ]);

      setDau(parseSeries(d));
      setWau(parseSeries(w));
      setMau(parseSeries(m));
      setNewUsers(parseSeries(nu));
      setSessions(parseSeries(sess));
      setSessionLen(parseSeries(sl));
      setEvents(parseSeries(ev));
      setRetention(ret?.data?.data ?? null);
    } catch (e) {
      toast.error(String((e as Error)?.message ?? e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    checkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status === "ok") loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, params.start, params.end]);

  const dauNow = dau.y.length ? dau.y[dau.y.length - 1] : 0;
  const wauNow = wau.y.length ? wau.y[wau.y.length - 1] : 0;
  const mauNow = mau.y.length ? mau.y[mau.y.length - 1] : 0;
  const totalEvents = sumLast(events.y);
  const totalSessions = sumLast(sessions.y);
  const avgSessionLen = avgLast(sessionLen.y);

  const chartData = (x: string[], y: number[], label: string) =>
    x.map((d, i) => ({ date: d, [label]: y[i] ?? 0 }));

  const mergedActive = dau.x.map((d, i) => ({
    date: d,
    DAU: dau.y[i] ?? 0,
    WAU: wau.y[i] ?? 0,
    MAU: mau.y[i] ?? 0,
  }));

  return (
    <PageContent
      title={t("title", "Amplitude Analytics")}
      items={[{ label: t("dashboard", "Dashboard"), href: "/" }]}
    >
      <div className="min-h-screen bg-[#1A1A1A] text-white">
        {/* Header */}
        <div className="sticky top-16 z-20 bg-[#1A1A1A] py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-3">
            {status === "checking" && (
              <Badge variant="outline" className="gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                {t("checking", "Verificando conexão...")}
              </Badge>
            )}
            {status === "ok" && (
              <Badge className="gap-1 bg-emerald-600 hover:bg-emerald-600">
                <CheckCircle2 className="h-3 w-3" />
                {t("connected", "Conectado")}
              </Badge>
            )}
            {status === "error" && (
              <Badge variant="destructive" className="gap-1">
                <AlertCircle className="h-3 w-3" />
                {t("disconnected", "Desconectado")}
              </Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                checkStatus();
              }}
              className="text-white hover:bg-white/10"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              {t("recheck", "Reverificar")}
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <DateRangePicker
              className="w-full md:w-[280px]"
              dateRange={dateRange}
              setDateRange={(r) => r && setDateRange(r)}
              presets={presets}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={loadAll}
              disabled={loading || status !== "ok"}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1" />
              )}
              {t("refresh", "Atualizar")}
            </Button>
          </div>
        </div>

        {status === "error" && (
          <Card className="bg-red-950/20 border-red-900 mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-300">
                <AlertCircle className="h-4 w-4" />
                {t("connectionError", "Erro de conexão com Amplitude")}
              </CardTitle>
              <CardDescription className="text-red-200/80">
                {statusError ?? t("checkCredentials", "Verifique as credenciais.")}
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <KpiCard icon={<Users className="h-4 w-4" />} label="DAU" value={dauNow} />
          <KpiCard icon={<Users className="h-4 w-4" />} label="WAU" value={wauNow} />
          <KpiCard icon={<Users className="h-4 w-4" />} label="MAU" value={mauNow} />
          <KpiCard icon={<TrendingUp className="h-4 w-4" />} label={t("newUsers", "Novos usuários")} value={sumLast(newUsers.y)} />
          <KpiCard icon={<Activity className="h-4 w-4" />} label={t("sessions", "Sessões")} value={totalSessions} />
          <KpiCard icon={<Clock className="h-4 w-4" />} label={t("avgSession", "Sessão média (s)")} value={avgSessionLen} />
        </div>

        <Tabs defaultValue="active" className="w-full">
          <TabsList className="bg-white/5">
            <TabsTrigger value="active">{t("activeUsers", "Usuários ativos")}</TabsTrigger>
            <TabsTrigger value="events">{t("eventsSessions", "Eventos & Sessões")}</TabsTrigger>
            <TabsTrigger value="retention">{t("retention", "Retenção")}</TabsTrigger>
          </TabsList>

          <TabsContent value="active" className="mt-4">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle>{t("activeUsersOverTime", "Usuários ativos ao longo do tempo")}</CardTitle>
                <CardDescription>DAU / WAU / MAU</CardDescription>
              </CardHeader>
              <CardContent className="h-[360px]">
                {loading ? (
                  <div className="h-full grid place-items-center">
                    <Loader2 className="h-6 w-6 animate-spin opacity-60" />
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={mergedActive}>
                      <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                      <XAxis dataKey="date" stroke="#888" />
                      <YAxis stroke="#888" />
                      <Tooltip contentStyle={{ background: "#222", border: "1px solid #444" }} />
                      <Legend />
                      <Line type="monotone" dataKey="DAU" stroke="#60a5fa" dot={false} />
                      <Line type="monotone" dataKey="WAU" stroke="#34d399" dot={false} />
                      <Line type="monotone" dataKey="MAU" stroke="#fbbf24" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-white/10 mt-4">
              <CardHeader>
                <CardTitle>{t("newUsersTrend", "Novos usuários (diário)")}</CardTitle>
              </CardHeader>
              <CardContent className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData(newUsers.x, newUsers.y, "newUsers")}>
                    <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                    <XAxis dataKey="date" stroke="#888" />
                    <YAxis stroke="#888" />
                    <Tooltip contentStyle={{ background: "#222", border: "1px solid #444" }} />
                    <Line type="monotone" dataKey="newUsers" stroke="#a78bfa" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="events" className="mt-4 space-y-4">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle>{t("eventsTotal", "Total de eventos por dia")}</CardTitle>
                <CardDescription>[Amplitude] Any Active Event</CardDescription>
              </CardHeader>
              <CardContent className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData(events.x, events.y, "events")}>
                    <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                    <XAxis dataKey="date" stroke="#888" />
                    <YAxis stroke="#888" />
                    <Tooltip contentStyle={{ background: "#222", border: "1px solid #444" }} />
                    <Line type="monotone" dataKey="events" stroke="#f472b6" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle>{t("sessionsPerDay", "Sessões por dia")}</CardTitle>
              </CardHeader>
              <CardContent className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData(sessions.x, sessions.y, "sessions")}>
                    <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                    <XAxis dataKey="date" stroke="#888" />
                    <YAxis stroke="#888" />
                    <Tooltip contentStyle={{ background: "#222", border: "1px solid #444" }} />
                    <Line type="monotone" dataKey="sessions" stroke="#22d3ee" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle>{t("avgSessionLength", "Duração média de sessão (segundos)")}</CardTitle>
              </CardHeader>
              <CardContent className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData(sessionLen.x, sessionLen.y, "len")}>
                    <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                    <XAxis dataKey="date" stroke="#888" />
                    <YAxis stroke="#888" />
                    <Tooltip contentStyle={{ background: "#222", border: "1px solid #444" }} />
                    <Line type="monotone" dataKey="len" stroke="#fb923c" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="retention" className="mt-4">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle>{t("retentionTitle", "Retenção N-day")}</CardTitle>
                <CardDescription>
                  {t("retentionDesc", "% de usuários que retornam após o dia 0")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!retention ? (
                  <p className="text-sm text-white/60">{t("noData", "Sem dados")}</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-white/70">
                          <th className="py-2 pr-4">{t("cohort", "Coorte")}</th>
                          <th className="py-2 pr-4">{t("size", "Tamanho")}</th>
                          {(retention.r_values?.[0] ?? []).map((_, i) => (
                            <th key={i} className="py-2 pr-4">D{i}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(retention.dates ?? []).map((d, i) => (
                          <tr key={d} className="border-t border-white/10">
                            <td className="py-2 pr-4">{d}</td>
                            <td className="py-2 pr-4">{retention.cohort_sizes?.[i] ?? 0}</td>
                            {(retention.r_values?.[i] ?? []).map((v, j) => (
                              <td key={j} className="py-2 pr-4">
                                {typeof v === "number" ? `${(v * 100).toFixed(1)}%` : "-"}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </PageContent>
  );
}

function KpiCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
}) {
  return (
    <Card className="bg-white/5 border-white/10">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-white/70 text-xs mb-1">
          {icon}
          <span>{label}</span>
        </div>
        <div className="text-2xl font-semibold">{value.toLocaleString()}</div>
      </CardContent>
    </Card>
  );
}
